import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import Box from './ProgramBulletBox';

function ProgramExplanation() {
    const { t } = useTranslation();
    const [currentIndex, setCurrentIndex] = useState(0);
    const trackRef = useRef(null);
    const boxCount = 5; // Total number of <Box /> components

    useEffect(() => {
        const track = trackRef.current;
        if (!track || !track.children || track.children.length === 0) return;
        
        const card = track.children[currentIndex];
        if (!card) return;
        
        const cardWidth = card.getBoundingClientRect().width + 4; // Add gap spacing
        const containerWidth = track.parentElement.getBoundingClientRect().width;
    
        // Calculate the offset to center the card
        const offset = (containerWidth - cardWidth) / 2;
    
        // Apply the transform with proper centering
        track.style.transform = `translateX(${Math.round(offset - currentIndex * cardWidth)}px)`;
    }, [currentIndex]);
    
    const handleTouch = (() => {
        let startX = 0;
        return {
            start: (e) => (startX = e.touches[0].clientX),
            end: (e) => {
                const endX = e.changedTouches[0].clientX;
                const diff = startX - endX;

                // Swipe logic: move left or right
                if (diff > 50 && currentIndex < boxCount - 1) {
                    setCurrentIndex((prev) => prev + 1);
                } else if (diff < -50 && currentIndex > 0) {
                    setCurrentIndex((prev) => prev - 1);
                }
            },
        };
    })();

    const handleIndicatorClick = (index) => setCurrentIndex(index);

    const strings = t('home.programExplanation.pills', { returnObjects: true });
    const programFeatures = t('home.programExplanation.features', { returnObjects: true });

    return (
        <section className="bg-gray-50 py-16 border-t border-b border-gray-100">
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
                <h2 className="text-xl sm:text-2xl font-extrabold text-[#162E3B] text-center mb-4">
                    {t('home.programExplanation.title')}
                </h2>

                {/* Modern pill-style badges for benefits */}
                <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto pt-3 pb-8">
                    {strings.map((item, index) => (
                        <span 
                            key={index} 
                            className="inline-flex items-center px-5 py-2.5 rounded-full text-sm sm:text-base font-bold bg-[#FFD82D] text-[#162E3B] shadow-sm border border-black/5"
                        >
                            {item}
                        </span>
                    ))}
                </div>

                {/* Static layout for larger screens */}
                <div className="hidden sm:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mt-10">
                    {programFeatures.map((feat, idx) => (
                        <Box
                            key={idx}
                            title={feat.title}
                            description={feat.description}
                        />
                    ))}
                </div>

                {/* Carousel for smaller screens */}
                <div className="carousel-container block sm:hidden pt-8">
                    <div className="carousel overflow-hidden">
                        <div
                            className="carousel-track flex transition-transform duration-300 ease-out"
                            ref={trackRef}
                            onTouchStart={handleTouch.start}
                            onTouchEnd={handleTouch.end}
                        >
                            {programFeatures.map((feat, idx) => (
                                <div key={idx} className="w-[85vw] flex-shrink-0 px-2">
                                    <Box
                                        title={feat.title}
                                        description={feat.description}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Carousel navigation with senior-friendly touch targets */}
                    <div className="carousel-navigation flex items-center justify-center gap-6 mt-8">
                        <button
                          className="w-10 h-10 flex items-center justify-center rounded-full bg-[#162E3B] text-white hover:bg-[#FFD82D] hover:text-[#162E3B] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-base font-bold shadow-md transition-all focus:outline-none"
                          aria-label={t('home.programExplanation.prevSlide')}
                          onClick={() => setCurrentIndex((prev) => Math.max(prev - 1, 0))}
                          disabled={currentIndex === 0}
                        >
                          &larr;
                        </button>

                        <div className="carousel-indicators flex gap-2.5">
                            {Array.from({ length: boxCount }).map((_, index) => (
                                <button
                                    key={index}
                                    className={`w-2 h-2 rounded-full transition-all focus:outline-none ${
                                        currentIndex === index
                                          ? "bg-[#FFD82D] scale-125 ring-2 ring-[#162E3B]/20"
                                          : "bg-gray-300 hover:bg-gray-400"
                                    }`}
                                    onClick={() => handleIndicatorClick(index)}
                                    aria-label={t('home.programExplanation.goToSlide', { number: index + 1 })}
                                ></button>
                            ))}
                        </div>

                        <button
                          className="w-10 h-10 flex items-center justify-center rounded-full bg-[#162E3B] text-white hover:bg-[#FFD82D] hover:text-[#162E3B] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-base font-bold shadow-md transition-all focus:outline-none"
                          aria-label={t('home.programExplanation.nextSlide')}
                          onClick={() =>
                              setCurrentIndex((prev) => Math.min(prev + 1, boxCount - 1))
                          }
                          disabled={currentIndex === boxCount - 1}
                        >
                          &rarr;
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default ProgramExplanation;
